import type { GfxConfig, MatchData } from '@/types'

const API_BASE_URL = import.meta.env.VITE_GFX_API_URL || 'https://test-fbgfx.frogbox.tv'

export interface TeamConfig {
  name: string
  shortName: string
  logoURL: string
}

export interface MatchConfig {
  currentStream: {
    customer: {
      countrycode: string
    }
  }
  homeTeamConfig: TeamConfig
  awayTeamConfig: TeamConfig
  gfxConfig: GfxConfig
  streamResolution: number
}

/**
 * Check if we're in demo mode based on customerId
 */
export const isDemo = (customerId: string): boolean => {
  return customerId === 'demo-customer'
}

/**
 * Fallback when no DB-backed match config exists for the
 * customer / event. Lets the score bug render from the JSON match
 * data + S3 score blob alone — team names + colours come from those
 * sources; logos / image slots stay empty. Without this, a missing
 * config surfaces as a full-page error overlay even though the score
 * payload is perfectly valid.
 */
const EMPTY_CONFIG: MatchConfig = {
  currentStream: { customer: { countrycode: '' } },
  homeTeamConfig: { name: '', shortName: '', logoURL: '' },
  awayTeamConfig: { name: '', shortName: '', logoURL: '' },
  gfxConfig: { imageSlots: [] },
  streamResolution: 1080,
}

export const fetchMatchConfig = async (customerId: string, eventGuid: string): Promise<MatchConfig> => {
  try {
    // Demo mode: fetch from local demo config
    if (isDemo(customerId)) {
      const response = await fetch('/demo/config.json')
      if (response.ok) return response.json()
      console.warn(`[matchConfig] demo config asset missing (${response.status}); rendering with empty config`)
      return EMPTY_CONFIG
    }

    // Production mode: fetch from API
    const response = await fetch(`${API_BASE_URL}/event/${customerId}/${eventGuid}`)
    if (response.ok) return response.json()

    // No DB-backed config for this customer/event. The score bug can
    // still render from the JSON match data + S3 score blob — team
    // names + colours come from there. Logos / image slots stay empty.
    console.warn(`[matchConfig] ${response.status} for ${customerId}/${eventGuid}; rendering with empty config`)
    return EMPTY_CONFIG
  } catch (error) {
    console.warn('[matchConfig] fetch threw; rendering with empty config', error)
    return EMPTY_CONFIG
  }
}

export const fetchMatchData = async (eventGuid: string): Promise<MatchData> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/match/${eventGuid}`)

    if (!response.ok) {
      throw new Error(`Failed to fetch match data: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching match data:', error)
    throw error
  }
}
